import React, { useState } from 'react';
import skillApis from '../../apis/skills';
import ToastMessage from '../../components/ToastMessage';
import { defaultAcademicYear } from '../../constants/academicYears';
import { SnackbarSeverityType } from '../../enums';
import { SkillResponse, SkillsManagementPageProps } from './types';
import { Title } from '../common/styled';
import SkillsTableAndActions from './components/SkillsTableAndActions';
import SkillsSearchFilters from './components/SkillsSearchFilters';
import useClassOptions from './hooks/useClassOptions';

const SkillsManagementPage: React.FC<SkillsManagementPageProps> = ({
  allClasses,
  school,
  isClassesLoading
}) => {
  const [academicYear, setAcademicYear] = useState(defaultAcademicYear);
  const [grade, setGrade] = useState('');
  const [division, setDivision] = useState('');
  const [skills, setSkills] = useState<SkillResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const { gradeOptions, divisionOptions } = useClassOptions(allClasses, grade);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleAcademicYearChange = (value: string) => {
    setAcademicYear(value);
    setGrade('');
    setDivision('');
    setHasSearched(false);
    setSkills([]);
  };

  const handleGradeChange = (value: string) => {
    setGrade(value);
    setDivision('');
    setHasSearched(false);
    setSkills([]);
  };

  const handleDivisionChange = (value: string) => {
    setDivision(value);
    setHasSearched(false);
    setSkills([]);
  };

  const handleSearch = () => {
    if (grade && division && academicYear && school) {
      setHasSearched(true);
      setIsLoading(true);
      skillApis
        .getSkills(grade, division, academicYear, school)
        .then((response: any) => {
          setSkills(response as SkillResponse[]);
        })
        .catch(() => {
          setSkills([]);
          showToast('Failed to load skills.', SnackbarSeverityType.error);
        })
        .finally(() => setIsLoading(false));
    } else {
      setSkills([]);
      setHasSearched(false);
    }
  };

  return (
    <>
      <Title>Skills Management</Title>
      <SkillsSearchFilters
        academicYear={academicYear}
        grade={grade}
        division={division}
        gradeOptions={gradeOptions}
        divisionOptions={divisionOptions}
        isClassesLoading={isClassesLoading}
        isLoading={isLoading}
        onAcademicYearChange={handleAcademicYearChange}
        onGradeChange={handleGradeChange}
        onDivisionChange={handleDivisionChange}
        onSearch={handleSearch}
      />
      {hasSearched && !isLoading && (
        <>
          <SkillsTableAndActions
            skills={skills}
            academicYear={academicYear}
            grade={grade}
            division={division}
            allClasses={allClasses}
            school={school}
            onSkillAdded={handleSearch}
            onSkillsCloned={handleSearch}
            onSkillUpdated={handleSearch}
            showToast={showToast}
          />
        </>
      )}
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default SkillsManagementPage;
